import React, {useState, useEffect} from 'react'
import DiscardChangesDialog from '../../DiscardChangesDialog'
import Grid from '@mui/material/Grid'
import useFormChanged, {NonEmptyValidator} from '../../hooks/useFormChanged'
import {spaceExtraLarge} from '../../../assets/styles/spacing'
import RXRButton from '../../RXRButton'
import {makeStyles} from '@mui/styles'
import {useDispatch} from 'react-redux'
import PropTypes from 'prop-types'
import MultilineTextInput from '../../MultilineTextInput'
import MessageConstants from '../../../components/Messages/MessageConstant'
import {useSelector} from 'react-redux'
import DashboardToggle from '../../DashboardToggle'
import EditDeskHours from './EditDeskHours'
import {DayMap} from './chatSettingConstants'
import {setBuildingData} from '../../../actions/buildingsActions'

const EMPTY_STATE = {
  outOfOfficeMessage: MessageConstants.DEFAULT_OOO_MESSAGE,
  deskHoursEnabled: false,
  schedule: DayMap.map((day, index) => {
    return {dayOfWeek: index, openTime: 540, closeTime: 1020} // default 9am to 5pm
  }),
  exceptions: [],
}

function EditBuildingChatSettings(props) {
  const classes = useStyles()
  const [isSaving, setIsSaving] = useState(false)
  const building = useSelector(state => state.GlobalAdmin.buildingsLookup[props.buildingId])
  const dispatch = useDispatch()

  // initialize our form controls
  const {invalidItems, formChanged, setForm, form, validateForm, resetInitialForm} = useFormChanged(
    EMPTY_STATE,
    useFormChanged.PropLevelValidation({
      schedule: (schedule, form) => {
        // if the hours are not enabled, then it's fine
        if (!form.deskHoursEnabled) {
          return true
        }

        // validate schedule and exceptions
        if (!Array.isArray(schedule) || schedule.length < 7) {
          return false
        }

        return schedule.every(
          day => NonEmptyValidator(day.dayOfWeek) && NonEmptyValidator(day.openTime) && NonEmptyValidator(day.closeTime),
        )
      },
      exceptions: (exceptions, form) => {
        // if the hours are not enabled, then it's fine
        if (!form.deskHoursEnabled) {
          return true
        }

        if (!Array.isArray(exceptions)) {
          return false
        }

        return exceptions.every(
          exception =>
            (NonEmptyValidator(exception.date) && exception.isClosedAllDay === true) ||
            (NonEmptyValidator(exception.openTime) && NonEmptyValidator(exception.closeTime)),
        )
      },
    }),
  )

  useEffect(() => {
    if (!props.buildingId || !building) {
      return
    }
    resetInitialForm({
      outOfOfficeMessage: building.outOfOfficeMessage || EMPTY_STATE.outOfOfficeMessage,
      deskHoursEnabled: building.deskHoursEnabled || EMPTY_STATE.deskHoursEnabled,
      schedule: building.deskHours?.schedule || EMPTY_STATE.schedule,
      exceptions: building.deskHours?.exceptions || EMPTY_STATE.exceptions,
    })
  }, [props.buildingId])

  const handleSave = async () => {
    if (!validateForm()) {
      return
    }

    setIsSaving(true)

    const updatedBuilding = {
      outOfOfficeMessage: form.outOfOfficeMessage,
      deskHoursEnabled: form.deskHoursEnabled,
    }

    if (form.deskHoursEnabled) {
      // we want to be explicit about what props we're sending
      updatedBuilding.deskHours = {
        schedule: form.schedule.map(day => ({
          dayOfWeek: day.dayOfWeek,
          openTime: day.openTime,
          closeTime: day.closeTime,
        })),
        exceptions: form.exceptions.map(e => ({
          date: e.date,
          openTime: e.isClosedAllDay ? 0 : e.openTime, // set to 0 to be safe
          closeTime: e.isClosedAllDay ? 0 : e.closeTime, // set to 0 to be safe
          isClosedAllDay: e.isClosedAllDay,
        })),
      }
    }

    try {
      await setBuildingData(dispatch, props.buildingId, updatedBuilding)
      resetInitialForm(form)
      if (props.onSave) {
        props.onSave()
      }
    } catch (err) {
      window.alert('There was an error saving the building chat settings. Please try again.')
      console.error('Error saving building chat settings:', err)
    }

    setIsSaving(false)
  }

  if (!building) {
    return null
  }

  return (
    <div className={classes.container}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <MultilineTextInput
            label="Out of office message"
            placeholder=""
            rows={4}
            rowsMax={4}
            isRequired={true}
            maxLength={2000}
            onChange={m => setForm({outOfOfficeMessage: m})}
            value={form.outOfOfficeMessage}
            helperText="This message will appear to users who try to send a chat when no agents are available."
          />
        </Grid>
        <Grid item xs={12}>
          <DashboardToggle
            label={form.deskHoursEnabled ? 'Disable chat schedule' : 'Enable chat schedule'}
            onChange={v => setForm({deskHoursEnabled: v})}
            value={form.deskHoursEnabled}
          />
          <p>If enabled, the out of office notice will automatically be set based on the configured time of day.</p>
        </Grid>
        {form.deskHoursEnabled && (
          <Grid item xs={12}>
            <EditDeskHours
              scheduleErrors={invalidItems.includes('schedule')}
              exceptionsErrors={invalidItems.includes('exceptions')}
              schedule={form.schedule}
              exceptions={form.exceptions}
              onChange={dh => setForm(dh)}
            />
          </Grid>
        )}
      </Grid>

      <div style={{flex: 1}}></div>
      <div style={{marginTop: spaceExtraLarge, flexShrink: 0}}>
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <RXRButton onClick={handleSave} isLoading={isSaving} disabled={!formChanged}>
              Save
            </RXRButton>
          </Grid>

          <Grid item xs={4} align="center">
            <RXRButton onClick={props.onCancel} isLoading={isSaving} type={RXRButton.TYPE_TEXT}>
              Cancel
            </RXRButton>
          </Grid>
        </Grid>
      </div>

      <DiscardChangesDialog hasChanges={formChanged} />
    </div>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
}))

EditBuildingChatSettings.propTypes = {
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  buildingId: PropTypes.string,
}

export default EditBuildingChatSettings
